import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  RadioGroup,
  FormControlLabel,
  TextField,
  Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Switch, Radio } from '@mui/material';
import BusinessHoursSettingModal from './BusinessHoursSettingModal';
import SaveConfirmModal from './SaveConfirmModal';

// iOS 스타일 토글 스위치
const IOSSwitch = styled(props => <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />)(
  ({ theme }) => ({
    width: 40,
    height: 20,
    padding: 0,
    '& .MuiSwitch-switchBase': {
      padding: 0,
      margin: 2,
      transitionDuration: '300ms',
      '&.Mui-checked': {
        transform: 'translateX(20px)',
        color: '#fff',
        '& + .MuiSwitch-track': {
          backgroundColor: '#0F7F85',
          opacity: 1,
          border: 0,
        },
      },
    },
    '& .MuiSwitch-thumb': {
      boxSizing: 'border-box',
      width: 16,
      height: 16,
    },
    '& .MuiSwitch-track': {
      borderRadius: 10,
      backgroundColor: '#E9E9EA',
      opacity: 1,
      transition: theme.transitions.create(['background-color'], {
        duration: 500,
      }),
    },
  }),
);

// 커스텀 라디오 버튼 스타일
const CustomRadio = styled(Radio)({
  padding: 0,
  '&.MuiRadio-root': {
    color: '#D9D9D9',
  },
  '&.Mui-checked': {
    color: '#0F7F85',
  },
  '& .MuiSvgIcon-root': {
    fontSize: 24,
  },
});

// 설정 행 컴포넌트
const SettingRow = ({ label, children, labelWidth = '160px' }) => (
  <Box sx={{ display: 'flex', gap: '40px', alignItems: 'flex-start' }}>
    <Typography
      sx={{
        width: labelWidth,
        minHeight: '40px',
        display: 'flex',
        alignItems: 'center',
        fontSize: '16px',
        fontWeight: 500,
        color: '#262626',
      }}>
      {label}
    </Typography>
    <Box sx={{ width: '462px' }}>{children}</Box>
  </Box>
);

// 미적용 배지 컴포넌트
const DisabledBadge = () => (
  <Box
    sx={{
      backgroundColor: '#D9D9D9',
      borderRadius: '999px',
      padding: '4px 16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
    <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#262626' }}>미적용</Typography>
  </Box>
);

// 적용 배지 컴포넌트 (클릭 가능)
const AppliedBadge = ({ onClick, disabled }) => (
  <Box
    onClick={disabled ? undefined : onClick}
    sx={{
      backgroundColor: '#0F7F85',
      borderRadius: '999px',
      padding: '4px 16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: disabled ? 'default' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      '&:hover': {
        backgroundColor: disabled ? '#0F7F85' : '#0D6A6F',
      },
    }}>
    <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#FFFFFF' }}>적용</Typography>
  </Box>
);

const ReservationEnabledPanel = ({
  advanceReservation,
  setAdvanceReservation,
  realtimeReservation,
  setRealtimeReservation,
  shortTimeReservation,
  setShortTimeReservation,
  advanceCustomSchedule,
  setAdvanceCustomSchedule,
  realtimeCustomSchedule,
  setRealtimeCustomSchedule,
  onSave,
}) => {
  // 모달 상태
  const [advanceHoursModalOpen, setAdvanceHoursModalOpen] = useState(false);
  const [realtimeHoursModalOpen, setRealtimeHoursModalOpen] = useState(false);
  const [saveConfirmModalOpen, setSaveConfirmModalOpen] = useState(false);

  // 오늘 기준 날짜 계산
  const dateRange = useMemo(() => {
    const today = new Date();
    const days = parseInt(advanceReservation.reservationDays) || 0;
    const endDate = new Date(today);
    endDate.setDate(today.getDate() + days);

    const formatDate = date => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dayNames = ['일', '월', '화', '수', '목', '금', '토'];
      const dayName = dayNames[date.getDay()];
      return `${year}-${month}-${day}(${dayName})`;
    };

    return `${formatDate(today)} ~ ${formatDate(endDate)}`;
  }, [advanceReservation.reservationDays]);

  return (
    <>
      {/* 예약 가능 설정 카드 */}
      <Box
        sx={{
          backgroundColor: '#FFFFFF',
          border: '1px solid #D9D9D9',
          borderRadius: '8px',
          padding: '32px',
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          width: '100%',
        }}>
        {/* 타이틀 */}
        <Typography sx={{ fontSize: '20px', fontWeight: 600, color: '#0F7F85' }}>회원앱에서 예약 가능</Typography>

        {/* 사전 예약 섹션 */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Typography sx={{ fontSize: '18px', fontWeight: 600, color: '#0F7F85' }}>사전 예약</Typography>
            <IOSSwitch
              checked={advanceReservation.enabled}
              onChange={e =>
                setAdvanceReservation(prev => ({
                  ...prev,
                  enabled: e.target.checked,
                }))
              }
            />
          </Box>

          {/* 사전 예약 하위 설정 */}
          <Box sx={{ opacity: advanceReservation.enabled ? 1 : 0.5, pointerEvents: advanceReservation.enabled ? 'auto' : 'none' }}>
            {/* 예약 가능 기간 */}
            <SettingRow label="예약 가능 기간">
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Typography sx={{ fontSize: '16px', fontWeight: 500, color: '#262626' }}>현재 시간 기준</Typography>
                  <TextField
                    value={advanceReservation.reservationDays}
                    onChange={e =>
                      setAdvanceReservation(prev => ({
                        ...prev,
                        // 숫자만. 음수·문자는 그대로 저장돼 DB 에 -5 가 들어가던 문제
                        reservationDays: e.target.value.replace(/\D/g, ''),
                      }))
                    }
                    disabled={!advanceReservation.enabled}
                    size="small"
                    sx={{
                      width: '60px',
                      '& .MuiOutlinedInput-root': {
                        height: '48px',
                        borderRadius: '6px',
                      },
                    }}
                    inputProps={{ style: { textAlign: 'center' } }}
                  />
                  <Typography sx={{ fontSize: '16px', fontWeight: 500, color: '#262626' }}>일</Typography>
                  <Typography sx={{ fontSize: '16px', fontWeight: 500, color: '#262626' }}>이내 까지</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Typography sx={{ fontSize: '16px', fontWeight: 500, color: '#262626' }}>오늘 기준</Typography>
                  <Typography sx={{ fontSize: '16px', fontWeight: 500, color: '#0F7F85' }}>{dateRange}</Typography>
                </Box>
              </Box>
            </SettingRow>

            {/* 예약 가능 요일 및 시간대 */}
            <Box sx={{ mt: '16px' }}>
              <SettingRow label="예약 가능 요일 및 시간대">
                <RadioGroup
                  value={advanceReservation.timeSlotOption}
                  onChange={e => {
                    if (!advanceReservation.enabled) return;
                    const newValue = e.target.value;
                    if (newValue === 'custom') {
                      setAdvanceHoursModalOpen(true);
                    }
                    setAdvanceReservation(prev => ({
                      ...prev,
                      timeSlotOption: newValue,
                    }));
                  }}>
                  <FormControlLabel
                    value="branchHours"
                    control={<CustomRadio disabled={!advanceReservation.enabled} />}
                    label="지점 운영시간 설정과 동일"
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        fontSize: '16px',
                        fontWeight: 500,
                        color: '#262626',
                        marginLeft: '8px',
                      },
                    }}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', height: '40px' }}>
                    <FormControlLabel
                      value="custom"
                      control={<CustomRadio disabled={!advanceReservation.enabled} />}
                      label="직접입력"
                      sx={{
                        '& .MuiFormControlLabel-label': {
                          fontSize: '16px',
                          fontWeight: 500,
                          color: '#262626',
                          marginLeft: '8px',
                        },
                      }}
                    />
                    {advanceCustomSchedule ? (
                      <AppliedBadge
                        onClick={() => setAdvanceHoursModalOpen(true)}
                        disabled={!advanceReservation.enabled}
                      />
                    ) : (
                      advanceReservation.timeSlotOption !== 'custom' && <DisabledBadge />
                    )}
                  </Box>
                </RadioGroup>
              </SettingRow>
            </Box>

            {/* 예약 가능 시간 표시 단위 */}
            <Box sx={{ mt: '16px' }}>
              <SettingRow label="예약 가능 시간 표시 단위">
                <RadioGroup
                  row
                  value={advanceReservation.timeUnitMinutes}
                  onChange={e => {
                    if (!advanceReservation.enabled) return;
                    setAdvanceReservation(prev => ({
                      ...prev,
                      timeUnitMinutes: e.target.value,
                    }));
                  }}
                  sx={{ gap: '16px' }}>
                  {['10', '15', '20', '30', '60'].map(value => (
                    <FormControlLabel
                      key={value}
                      value={value}
                      control={<CustomRadio disabled={!advanceReservation.enabled} />}
                      label={`${value}분`}
                      sx={{
                        marginRight: 0,
                        '& .MuiFormControlLabel-label': {
                          fontSize: '16px',
                          fontWeight: 500,
                          color: '#262626',
                          marginLeft: '8px',
                        },
                      }}
                    />
                  ))}
                </RadioGroup>
              </SettingRow>
            </Box>

            {/* 예약 불가능 시간 */}
            <Box sx={{ mt: '16px' }}>
              <SettingRow label="예약 불가능 시간">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Typography sx={{ fontSize: '16px', fontWeight: 500, color: '#262626' }}>현재 시간 기준</Typography>
                  <TextField
                    value={advanceReservation.unavailableHours}
                    onChange={e =>
                      setAdvanceReservation(prev => ({
                        ...prev,
                        unavailableHours: e.target.value.replace(/\D/g, ''),
                      }))
                    }
                    disabled={!advanceReservation.enabled}
                    size="small"
                    sx={{
                      width: '60px',
                      '& .MuiOutlinedInput-root': {
                        height: '48px',
                        borderRadius: '6px',
                      },
                    }}
                    inputProps={{ style: { textAlign: 'center' } }}
                  />
                  <Typography sx={{ fontSize: '16px', fontWeight: 500, color: '#262626' }}>시</Typography>
                  <Typography sx={{ fontSize: '16px', fontWeight: 500, color: '#262626' }}>이내에는 예약 불가능</Typography>
                </Box>
              </SettingRow>
            </Box>
          </Box>
        </Box>

        <Divider />

        {/* 실시간 예약 섹션 */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Typography sx={{ fontSize: '18px', fontWeight: 600, color: '#0F7F85' }}>실시간 예약</Typography>
            <IOSSwitch
              checked={realtimeReservation.enabled}
              onChange={e =>
                setRealtimeReservation(prev => ({
                  ...prev,
                  enabled: e.target.checked,
                }))
              }
            />
          </Box>

          {/* 실시간 예약 하위 설정 */}
          <Box sx={{ opacity: realtimeReservation.enabled ? 1 : 0.5, pointerEvents: realtimeReservation.enabled ? 'auto' : 'none' }}>
            {/* 예약 가능 요일 및 시간대 */}
            <SettingRow label="예약 가능 요일 및 시간대">
              <RadioGroup
                value={realtimeReservation.timeSlotOption}
                onChange={e => {
                  if (!realtimeReservation.enabled) return;
                  const newValue = e.target.value;
                  if (newValue === 'custom') {
                    setRealtimeHoursModalOpen(true);
                  }
                  setRealtimeReservation(prev => ({
                    ...prev,
                    timeSlotOption: newValue,
                  }));
                }}>
                <FormControlLabel
                  value="branchHours"
                  control={<CustomRadio disabled={!realtimeReservation.enabled} />}
                  label="지점 운영시간 설정과 동일"
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontSize: '16px',
                      fontWeight: 500,
                      color: '#262626',
                      marginLeft: '8px',
                    },
                  }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', height: '40px' }}>
                  <FormControlLabel
                    value="custom"
                    control={<CustomRadio disabled={!realtimeReservation.enabled} />}
                    label="직접입력"
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        fontSize: '16px',
                        fontWeight: 500,
                        color: '#262626',
                        marginLeft: '8px',
                      },
                    }}
                  />
                  {realtimeCustomSchedule ? (
                    <AppliedBadge
                      onClick={() => setRealtimeHoursModalOpen(true)}
                      disabled={!realtimeReservation.enabled}
                    />
                  ) : (
                    realtimeReservation.timeSlotOption !== 'custom' && <DisabledBadge />
                  )}
                </Box>
              </RadioGroup>
            </SettingRow>
          </Box>
        </Box>

        <Divider />

        {/* 숏타임 예약 섹션 */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Typography sx={{ fontSize: '18px', fontWeight: 600, color: '#0F7F85' }}>숏타임 예약</Typography>
            <IOSSwitch
              checked={shortTimeReservation.enabled}
              onChange={e =>
                setShortTimeReservation(prev => ({
                  ...prev,
                  enabled: e.target.checked,
                }))
              }
            />
          </Box>
        </Box>
      </Box>

      {/* 설정 저장 버튼 */}
      <Button
        variant="contained"
        onClick={() => setSaveConfirmModalOpen(true)}
        sx={{
          width: '240px',
          height: '48px',
          backgroundColor: '#0F7F85',
          borderRadius: '8px',
          fontSize: '20px',
          fontWeight: 600,
          textTransform: 'none',
          '&:hover': {
            backgroundColor: '#0D6A6F',
          },
        }}>
        설정저장
      </Button>

      {/* 숏타임 설명 박스 */}
      <Box
        sx={{
          backgroundColor: '#F2F2F2',
          borderRadius: '8px',
          padding: '16px 32px',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={{ fontSize: '18px', fontWeight: 600, color: '#EE735A', lineHeight: 1.5 }}>
            숏타임이란?
          </Typography>
          <Typography sx={{ fontSize: '16px', fontWeight: 500, color: '#262626', lineHeight: 1.5 }}>
            회원이 이용권의 기본 이용시간보다 짧은 시간으로 타석(또는 코트 등)을 예약할 수 있는 기능입니다. 짧은 시간만
            이용하고 싶은 회원에게 유용하며,
            <br />
            센터는 이용 공간을 보다 효율적으로 운영할 수 있어요. 이용권 차감은 기존과 동일하게 적용됩니다.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={{ fontSize: '18px', fontWeight: 600, color: '#EE735A', lineHeight: 1.5 }}>
            회원앱 숏타임 기준
          </Typography>
          <Typography sx={{ fontSize: '16px', fontWeight: 500, color: '#262626' }}>
            : 15분 이상 ~ 각 종목의 이용권 기본 시간 미만
          </Typography>
        </Box>
      </Box>

      {/* 사전 예약 영업시간 설정 모달 */}
      <BusinessHoursSettingModal
        open={advanceHoursModalOpen}
        onClose={() => {
          setAdvanceHoursModalOpen(false);
          if (!advanceCustomSchedule) {
            setAdvanceReservation(prev => ({
              ...prev,
              timeSlotOption: 'branchHours',
            }));
          }
        }}
        title="사전 예약 시간 설정"
        initialSchedule={advanceCustomSchedule}
        onConfirm={schedule => {
          setAdvanceCustomSchedule(schedule);
          setAdvanceReservation(prev => ({
            ...prev,
            timeSlotOption: 'custom',
          }));
          setAdvanceHoursModalOpen(false);
        }}
      />

      {/* 실시간 예약 영업시간 설정 모달 */}
      <BusinessHoursSettingModal
        open={realtimeHoursModalOpen}
        onClose={() => {
          setRealtimeHoursModalOpen(false);
          if (!realtimeCustomSchedule) {
            setRealtimeReservation(prev => ({
              ...prev,
              timeSlotOption: 'branchHours',
            }));
          }
        }}
        title="실시간 예약 시간 설정"
        initialSchedule={realtimeCustomSchedule}
        onConfirm={schedule => {
          setRealtimeCustomSchedule(schedule);
          setRealtimeReservation(prev => ({
            ...prev,
            timeSlotOption: 'custom',
          }));
          setRealtimeHoursModalOpen(false);
        }}
      />

      {/* 설정 저장 확인 모달 */}
      <SaveConfirmModal
        open={saveConfirmModalOpen}
        onClose={() => setSaveConfirmModalOpen(false)}
        onConfirm={() => {
          setSaveConfirmModalOpen(false);
          onSave();
        }}
      />
    </>
  );
};

export default ReservationEnabledPanel;
